import { useState } from 'react';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons/faChevronUp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';

import { detailSectionMessages } from './detail-section-messages';
import styles from './DetailSection.module.css';

import type { ReactNode } from 'react';

type DetailSectionProps = {
  title: string;
  children: ReactNode;
  isDefaultExpanded?: boolean;
  testId?: string;
};

/**
 * Reusable collapsible card shell shared by every freight detail card.
 *
 * A clickable header (title + chevron) toggles an expandable body. Open/closed
 * is UI-ephemeral state held locally — it carries no domain meaning, so it
 * lives here rather than in any store. Defaults to expanded.
 */
export const DetailSection = ({ title, children, isDefaultExpanded = true, testId = 'detail-section' }: DetailSectionProps) => {
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(isDefaultExpanded);

  const toggleLabel = intl.formatMessage(isExpanded ? detailSectionMessages.collapse : detailSectionMessages.expand, { title });

  return (
    <div className={styles.section} data-testid={testId}>
      <button
        aria-expanded={isExpanded}
        aria-label={toggleLabel}
        className={styles.header}
        onClick={() => setIsExpanded((prev) => !prev)}
        type='button'
      >
        <span className={styles.title}>{title}</span>
        <FontAwesomeIcon className={styles.chevron} icon={isExpanded ? faChevronUp : faChevronDown} />
      </button>
      {isExpanded && <div className={styles.body}>{children}</div>}
    </div>
  );
};
